import { Grid, Stack, MenuItem, TextField } from '@mui/material';
import CustomButton from 'components/Common/Button';
import TextInput from 'components/Common/TextInput';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { staffRoleRank, type StaffRoleType } from 'api/v1Helpers';
import { CreateStaffFormProps } from 'types/staff';

const ROLE_OPTIONS: { value: StaffRoleType; label: string }[] = [
    { value: 'CLERK', label: 'Clerk' },
    { value: 'MERCHANT_ADMIN', label: 'Admin' },
    { value: 'PROVIDER_ADMIN', label: 'Provider Admin' },
];

const provisionSchema = yup
    .object({
        firstName: yup.string().required('First name is required').trim().min(1).max(100),
        lastName: yup.string().required('Last name is required').trim().min(1).max(100),
        email: yup.string().required('Email is required').email('Enter a valid email').lowercase().trim(),
        role: yup.mixed<StaffRoleType>().oneOf(['CLERK', 'MERCHANT_ADMIN', 'PROVIDER_ADMIN']).required('Role is required'),
    })
    .required();

type ProvisionFormValues = {
    firstName: string;
    lastName: string;
    email: string;
    role: StaffRoleType;
};

/** Primary Create Staff form — provisions account via POST …/staff/accounts. */
const CreateStaffForm = ({ loading, onSubmit, submitButtonText, actorRole, showRoleSelect, submitDisabled }: CreateStaffFormProps) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<ProvisionFormValues>({
        resolver: yupResolver(provisionSchema),
        defaultValues: { firstName: '', lastName: '', email: '', role: 'CLERK' },
    });

    const availableRoles = showRoleSelect
        ? ROLE_OPTIONS.filter((r) => !actorRole || staffRoleRank(r.value) <= staffRoleRank(actorRole))
        : ROLE_OPTIONS;

    const onFormSubmit = (data: ProvisionFormValues) => {
        if (submitDisabled) return;
        onSubmit({
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: data.email.trim().toLowerCase(),
            role: data.role,
        });
    };

    return (
        <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
            <Grid container item xs={12} rowGap={2}>
                <Grid item xs={12} sm={6}>
                    <Controller
                        name="firstName"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                icon={null}
                                width="100%"
                                placeholder="First name"
                                label="First Name"
                                value={field.value}
                                onChange={field.onChange}
                                error={Boolean(errors.firstName)}
                                helperText={errors.firstName?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                icon={null}
                                width="100%"
                                placeholder="Last name"
                                label="Last Name"
                                value={field.value}
                                onChange={field.onChange}
                                error={Boolean(errors.lastName)}
                                helperText={errors.lastName?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                icon={null}
                                width="100%"
                                placeholder="Username@email.com"
                                label="Email"
                                value={field.value}
                                onChange={field.onChange}
                                error={Boolean(errors.email)}
                                helperText={errors.email?.message}
                            />
                        )}
                    />
                </Grid>

                {showRoleSelect && availableRoles.length > 0 && (
                    <Grid item xs={12}>
                        <Controller
                            name="role"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    select
                                    fullWidth
                                    label="Role"
                                    value={field.value}
                                    onChange={field.onChange}
                                    size="small"
                                    error={Boolean(errors.role)}
                                    helperText={errors.role?.message}
                                >
                                    {availableRoles.map((r) => (
                                        <MenuItem key={r.value} value={r.value}>
                                            {r.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />
                    </Grid>
                )}
            </Grid>
            <Stack justifyContent="center" direction="row" sx={{ mt: 4 }}>
                <CustomButton
                    title={submitButtonText || 'Create Staff'}
                    type="submit"
                    loading={loading}
                    disabled={submitDisabled}
                    sx={{ width: { xs: '90%', sm: '40%' } }}
                />
            </Stack>
        </form>
    );
};

export default CreateStaffForm;
